import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Sun, Moon, Printer, Download, RefreshCw, Info } from "react-feather";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetCompanyInfoQuery } from "../../settings/settingsApi";
import { useGetDutyListQuery } from "../guardingApi";
import GuardLookupTabs from "../components/GuardLookupTabs";
import DutyListGrid from "../dutyList/DutyListGrid";
import {
  DutyListTotals,
  DutyListSummaryTable,
  DutyListGuardsTable,
  DutyListNewTable,
  DutyListBlankTable,
} from "../dutyList/DutyListTables";
import GuardMonthModal from "../dutyList/GuardMonthModal";
import DutyListPrintModal from "../dutyList/DutyListPrintModal";
import { exportDutyListExcel } from "../dutyList/dutyListExport";
import {
  HISTORY_LAST_DAY,
  buildRows,
  guardMap,
  filterRows,
  summarizeCustomers,
  totalsOf,
  summarizeGuards,
  newAssignments,
  blankSlots,
  monthLabel,
  lastElapsedDay,
} from "../dutyList/dutyListModel";

const VIEWS = [
  { key: "detail", label: "Detail" },
  { key: "summary", label: "Summary" },
  // Everyone with duty on the lines, office staff included - hence not "Guards".
  { key: "guards", label: "Employees" },
  { key: "new", label: "New Assignments" },
  { key: "blank", label: "Blank" },
];

const SHIFTS = [
  { value: "", label: "All" },
  { value: "Day", label: "Day", icon: Sun },
  { value: "Night", label: "Night", icon: Moon },
];

const BLANK_WINDOWS = [
  { value: 0, label: "Whole month" },
  { value: 10, label: "Last 10 days" },
  { value: 3, label: "Last 3 days" },
  { value: 1, label: "Last day only" },
];

const thisMonthValue = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * Duty List (2026-09-14) - web version of the WinForms "Duty List" report
 * (Texas_Admin frm_assignment_details), beside Guard Lookup under Guarding.
 *
 * One call per branch and month (GET /attendance/duty-list, Batch171); every
 * filter and view after that is instant, client-side. Views: Detail (the
 * day-by-day grid), Summary (per customer), Employees (per person, click for
 * their month), New Assignments (started this month) and Blank (due shifts
 * with nobody on them - the WinForms "Show Blank"). Print and Excel take
 * whichever view is open, with its filters. The WinForms "Dog & Handler"
 * view was left out on request.
 */
const GuardDutyListPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [branchId, setBranchId] = useState(null);
  const [monthValue, setMonthValue] = useState(thisMonthValue);
  const [view, setView] = useState("detail");
  const [customer, setCustomer] = useState(null);
  const [area, setArea] = useState("");
  const [shift, setShift] = useState("");
  const [search, setSearch] = useState("");
  const [hideEmpty, setHideEmpty] = useState(false);
  const [onlyTemp, setOnlyTemp] = useState(false);
  const [blankWindow, setBlankWindow] = useState(10);
  const [pickedGuard, setPickedGuard] = useState(null);
  const [showPrint, setShowPrint] = useState(false);

  // Open on the header's branch (or the first one the user has when the header
  // says "All branches" - a duty list is always one branch, like the branch's
  // own Windows report).
  useEffect(() => {
    if (branchId == null && availableBranches.length) {
      setBranchId(selectedBranchId ?? availableBranches[0].branchId);
    }
  }, [branchId, selectedBranchId, availableBranches]);

  // ...and keep following it (2026-10-02): switching the header branch moves the duty
  // list to that branch. Under "All branches" the page's own select stays in charge.
  useEffect(() => {
    if (selectedBranchId != null) setBranchId(selectedBranchId);
  }, [selectedBranchId]);

  const [year, month] = monthValue.split("-").map(Number);
  const { data, isFetching, error, refetch } = useGetDutyListQuery(
    { branchId, year, month },
    { skip: branchId == null || !year || !month }
  );
  const { data: companyInfo } = useGetCompanyInfoQuery(branchId, { skip: branchId == null });
  const companyName = companyInfo?.["Company.Name"] || undefined;
  const branchCode = availableBranches.find((b) => b.branchId === branchId)?.branchCode;

  // A new branch or month is a new list - drop the old customer/area picks.
  useEffect(() => {
    setCustomer(null);
    setArea("");
  }, [branchId, monthValue]);

  const allRows = useMemo(() => buildRows(data), [data]);
  const guards = useMemo(() => guardMap(data), [data]);
  const customerOptions = useMemo(() => {
    const seen = new Map();
    allRows.forEach((r) => seen.set(r.custId, r.custName));
    return [...seen.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [allRows]);
  const areaOptions = useMemo(() => [...new Set(allRows.map((r) => r.area).filter(Boolean))].sort(), [allRows]);

  const rows = useMemo(
    () => filterRows(allRows, guards, { custId: customer?.value ?? null, area, shift, search, hideEmpty, onlyTemp }),
    [allRows, guards, customer, area, shift, search, hideEmpty, onlyTemp]
  );
  const totals = useMemo(() => totalsOf(rows), [rows]);
  const summary = useMemo(() => summarizeCustomers(rows), [rows]);
  const guardRows = useMemo(() => {
    const list = summarizeGuards(rows, guards);
    // In the Guards view a search that names a guard narrows to that guard,
    // not to everyone who shared a post with them.
    const term = search.trim().toLowerCase();
    const matching = term ? list.filter((g) => String(g.empCode).includes(term) || (g.name || "").toLowerCase().includes(term)) : [];
    return matching.length ? matching : list;
  }, [rows, guards, search]);
  const newRows = useMemo(() => (data ? newAssignments(rows, data) : []), [rows, data]);
  const blankRows = useMemo(() => (data ? blankSlots(rows, data, blankWindow) : []), [rows, data, blankWindow]);
  // A guard's month from every line of the branch, not just the filtered ones.
  const allGuardRows = useMemo(() => summarizeGuards(allRows, guards), [allRows, guards]);
  const guardForModal = pickedGuard != null ? allGuardRows.find((g) => g.empCode === pickedGuard) : null;

  const monthText = monthLabel(year, month);
  const lastDay = data ? lastElapsedDay(data) : 0;
  const blankWindowText =
    blankWindow && data
      ? `Blank shifts in the last ${blankWindow === 1 ? "day" : `${blankWindow} days`} up to ${Math.max(1, lastDay)} ${monthText}`
      : `Blank shifts in ${monthText}`;
  const subtitle = [
    monthText,
    branchCode,
    customer && `Customer: ${customer.label}`,
    area && `Area: ${area}`,
    shift && `${shift} shift`,
    onlyTemp && "Temp only",
    hideEmpty && "Lines with duty only",
    search.trim() && `Search: "${search.trim()}"`,
  ]
    .filter(Boolean)
    .join(" · ");

  const monthEndIso = `${monthValue}-${String(new Date(year, month, 0).getDate()).padStart(2, "0")}`;
  const showHistoryNote = monthEndIso > HISTORY_LAST_DAY;

  const exportProps = { view, data, rows, summary, guardRows, newRows, blankRows, subtitle, blankWindowText, companyName };
  const previewProps = { ...exportProps, guards, totals, branchCode };

  const openCustomer = (custId) => {
    const opt = customerOptions.find((o) => o.value === custId);
    if (opt) setCustomer(opt);
    setView("detail");
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Guard Duty List</h4>
              <h6>Who was on duty at each assignment, day by day</h6>
            </div>
          </div>
        </div>

        <GuardLookupTabs />

        <div className="card">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              {availableBranches.length > 1 && (
                <div className="col-6 col-md-2">
                  <label className="form-label small text-muted mb-1">Branch</label>
                  <select className="form-select" value={branchId ?? ""} onChange={(e) => setBranchId(Number(e.target.value))}>
                    {availableBranches.map((b) => (
                      <option key={b.branchId} value={b.branchId}>
                        {b.branchCode}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">Month</label>
                <input type="month" className="form-control" value={monthValue} onChange={(e) => e.target.value && setMonthValue(e.target.value)} />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label small text-muted mb-1">Customer</label>
                <Select
                  classNamePrefix="react-select"
                  options={customerOptions}
                  value={customer}
                  onChange={setCustomer}
                  isClearable
                  placeholder={data ? `All customers (${customerOptions.length})` : "Loading..."}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">Area</label>
                <select className="form-select" value={area} onChange={(e) => setArea(e.target.value)}>
                  <option value="">All areas</option>
                  {areaOptions.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-md-2">
                <button type="button" className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-1" onClick={refetch} disabled={isFetching || branchId == null}>
                  {isFetching ? <span className="spinner-border spinner-border-sm" /> : <RefreshCw size={15} />} Refresh
                </button>
              </div>
            </div>

            <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 260 }}
                placeholder="Search customer, assignment, guard name or code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="btn-group" role="group">
                {SHIFTS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`btn btn-sm rounded-pill me-1 ${shift === opt.value ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setShift(opt.value)}
                  >
                    {opt.icon ? <opt.icon size={13} className="me-1" /> : null}
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="form-check form-switch small mb-0">
                <input className="form-check-input" type="checkbox" id="dl-hide-empty" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
                <label className="form-check-label" htmlFor="dl-hide-empty">Lines with duty only</label>
              </div>
              <div className="form-check form-switch small mb-0">
                <input className="form-check-input" type="checkbox" id="dl-temp" checked={onlyTemp} onChange={(e) => setOnlyTemp(e.target.checked)} />
                <label className="form-check-label" htmlFor="dl-temp">Temp only</label>
              </div>
              {data?.isScopedToMyAreas && <span className="badge bg-info text-dark">Your areas only</span>}
              <div className="ms-auto d-flex gap-2">
                <button type="button" className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1" disabled={!data} onClick={() => exportDutyListExcel(exportProps)}>
                  <Download size={14} /> Excel
                </button>
                <button type="button" className="btn btn-sm btn-primary d-flex align-items-center gap-1" disabled={!data} onClick={() => setShowPrint(true)}>
                  <Printer size={14} /> Print
                </button>
              </div>
            </div>
          </div>
        </div>

        {showHistoryNote && (
          <div className="alert alert-warning d-flex align-items-start gap-2 py-2 small">
            <Info size={16} className="flex-shrink-0 mt-1" />
            <div>
              Duty marked in the Windows app is on the web only up to <b>2 Sep 2026</b>. After that only duty marked on the web shows here, so later days
              look blank even where the branch has them in the Windows Duty List.
            </div>
          </div>
        )}

        {error && <div className="alert alert-danger">{error?.data?.message || "Couldn't load the duty list. Please try again."}</div>}

        {data && (
          <>
            <ul className="nav nav-tabs duty-view-tabs">
              {VIEWS.map((v) => (
                <li key={v.key} className="nav-item">
                  <button type="button" className={`nav-link ${view === v.key ? "active" : ""}`} onClick={() => setView(v.key)}>
                    {v.label}
                    <span className="badge rounded-pill bg-light text-dark border ms-1">
                      {v.key === "detail"
                        ? rows.length
                        : v.key === "summary"
                        ? summary.length
                        : v.key === "guards"
                        ? guardRows.length
                        : v.key === "new"
                        ? newRows.length
                        : blankRows.length}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            <div className="card border-top-0 rounded-top-0">
              <div className={`card-body ${isFetching ? "opacity-50" : ""}`}>
                {view === "summary" && <DutyListTotals totals={totals} />}
                {view === "blank" && (
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2 small">
                    <span className="text-muted">Show blank shifts in:</span>
                    {BLANK_WINDOWS.map((w) => (
                      <button
                        key={w.value}
                        type="button"
                        className={`btn btn-sm rounded-pill ${blankWindow === w.value ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setBlankWindow(w.value)}
                      >
                        {w.label}
                      </button>
                    ))}
                    <span className="text-muted ms-2">{blankWindowText}</span>
                  </div>
                )}

                {view === "detail" && <DutyListGrid data={data} rows={rows} guards={guards} onPickGuard={setPickedGuard} />}
                {view === "summary" && <DutyListSummaryTable summary={summary} onPickCustomer={openCustomer} />}
                {view === "guards" && <DutyListGuardsTable guardRows={guardRows} onPickGuard={setPickedGuard} />}
                {view === "new" && <DutyListNewTable rows={newRows} />}
                {view === "blank" && <DutyListBlankTable rows={blankRows} />}
              </div>
            </div>
          </>
        )}

        {!data && isFetching && (
          <div className="text-center text-muted py-5">
            <span className="spinner-border spinner-border-sm me-2" /> Loading the duty list...
          </div>
        )}

        {guardForModal && <GuardMonthModal guard={guardForModal} monthText={monthText} onClose={() => setPickedGuard(null)} />}
        {showPrint && data && <DutyListPrintModal show onClose={() => setShowPrint(false)} previewProps={previewProps} />}
      </div>
    </div>
  );
};

export default GuardDutyListPage;
